"use client"
import styled from "@emotion/styled"
import { Box, Button, ButtonBase } from "@mui/material"
import Link from "next/link"
import { useEffect, useState } from "react"
import { Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { Container, Desktop, LeftArrow, NewText, RightArrow, useIsDesktop } from "ui"
import { extractProperty } from "utils"
import ProductCard from "./ProductCard"

const StyledContainer = styled(Container)`
  margin-top: 60px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 80px;
  }
`

const StyledSwiper = styled(Swiper)`
  padding: 0;
  padding-bottom: 32px;
  margin-top: 24px;
  padding-left: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: calc(100% - 20px);
    max-width: 100%;
    margin-right: auto;
    margin-left: 0;
    padding-left: 20px;
    padding-right: 16px;
  }
  .swiper-pagination {
    display: none;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  width: fit-content;
  :last-child {
    margin-right: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    :last-child {
      margin-right: 0;
    }
  }
`
const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
  padding-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 0px;
    padding-left: 20px;
    padding-bottom: 24px;
  }
`

const StyledButton = styled(Button)`
  background-color: transparent;
  color: black;
  border: 2px solid black;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

const BulletsFlex = styled.div`
  display: flex;
  gap: 6.67px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
`

const Bullet = styled.button<{ active: boolean }>`
  width: 10px;
  height: 10px;
  cursor: pointer;
  padding: 0;
  background-color: ${({ active, theme }) =>
    active ? theme.colors.buttonActiveBackgroundColor : "transparent"};
  border-radius: 200px;
  border: ${({ active }) => (!active ? "1.667px solid #000000" : "none")};
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 14px;
    height: 14px;
    border: ${({ active }) => (!active ? "2.333px solid #000000" : "none")};
  }
`
const StyledArrow = styled(ButtonBase)``

const ProductsSlider = ({
  data,
  locale,
  productsdata,
}: {
  data: any
  locale: string
  productsdata: any
}) => {
  const isDesktop = useIsDesktop()
  const title = extractProperty(data, "title")
  const productSubTitle = extractProperty(data, "subTitle")
  const [swiper, setSwiper] = useState(Object)
  const [activeIndex, setActiveIndex] = useState(0)

  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  type PropsBullet = {
    pagination: any
  }
  const PaginationBullets = ({ pagination }: PropsBullet) => {
    return (
      <BulletsFlex>
        <Desktop>
          <StyledArrow
            onClick={() => swiper.slidePrev()}
            aria-label={locale === "he" ? "לפריטים הקודמים בגלריה" : "Previous gallery items"}
          >
            {locale === "he" ? <RightArrow /> : <LeftArrow />}
          </StyledArrow>
        </Desktop>
        {pagination?.map((_: any, index: any) => (
          <Bullet
            active={index === activeIndex}
            key={index}
            // className={`bullet ${index === activeIndex ? "active" : ""}`}
            onClick={() => swiper?.slideTo(index)}
          />
        ))}
        <Desktop>
          <StyledArrow
            onClick={() => swiper.slideNext()}
            aria-label={locale === "he" ? "לפריטים הבאים בגלריה" : "Next gallery items"}
          >
            {locale === "he" ? <LeftArrow /> : <RightArrow />}
          </StyledArrow>
        </Desktop>
      </BulletsFlex>
    )
  }

  if (productsdata?.length === 0 || !isClient || !data) {
    return null
  }
  return (
    <>
      <StyledContainer>
        <NewText
          component={"h2"}
          desktopFontSize={32}
          desktopFontWeight={400}
          desktopLineHeight={"36px"}
          mobileFontSize={24}
          mobileFontWeight={400}
          mobileLineHeight={1}
          color="black"
          marginBottom={isDesktop ? "16px" : "8px"}
        >
          {title}
        </NewText>
        <NewText
          component={"h3"}
          desktopFontSize={24}
          desktopFontWeight={400}
          desktopLineHeight={1}
          mobileFontSize={18}
          mobileFontWeight={400}
          mobileLineHeight={1}
          color="black"
        >
          {productSubTitle}
        </NewText>
      </StyledContainer>
      <Container forSwiper>
        <StyledSwiper
          slidesPerView={isDesktop ? 4 : "auto"}
          spaceBetween={isDesktop ? "24px" : "16px"}
          modules={[Pagination, Navigation]}
          onSwiper={setSwiper}
          pagination
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        >
          {productsdata?.map((product: any, index: any) => {
            const url = product?.node.absoluteUrl?.split("pages")?.[1]
            const properties = product?.node.properties
            const name = properties?.find(
              (property: any) => property.propertyName === "productName",
            )?.propertyValue?.value
            const category = properties?.find(
              (property: any) => property.propertyName === "productCategory",
            )?.propertyValue?.value
            const subCategory = properties?.find(
              (property: any) => property.propertyName === "productSubCategory",
            )?.propertyValue?.value
            const image = properties?.find((property: any) => property.propertyName === "image")
              ?.propertyValue?.mediaItems?.[0]?.url
            return (
              <StyledSwiperSlide key={index}>
                <ProductCard
                  productTitle={name}
                  imageURL={image}
                  productURL={url}
                  productSubtitle={subCategory}
                  badge={category}
                  locale={locale}
                />
              </StyledSwiperSlide>
            )
          })}
        </StyledSwiper>
        <Box>
          <ButtonsContainer>
            <PaginationBullets pagination={swiper?.pagination?.bullets} />
            <Link href={"/products"}>
              <StyledButton variant="outlined" size="large">
                {"לכל המוצרים"}
              </StyledButton>
            </Link>
          </ButtonsContainer>
        </Box>
      </Container>
    </>
  )
}

export default ProductsSlider
